import {
  ApolloError,
  DocumentNode,
  LazyQueryHookOptions,
  OperationVariables,
  QueryResult,
  TypedDocumentNode,
  useLazyQuery,
} from '@apollo/client';
import { useRumContext } from 'services/observability/datadog/RumComponentContext';
import { createErrorMonitor } from 'services/observability/utilities';

export type FetchFn<QueryData, QueryVariables> = (
  variables?: QueryVariables,
) => Promise<QueryResult<QueryData, QueryVariables>>;

export interface EnhancedLazyGraphqlResult<QueryData, QueryVariables> {
  data: QueryData | undefined;
  previousData: QueryData | undefined;
  error: boolean;
  errorMessage: ApolloError | undefined;
  isLoading: boolean;
  fetch: FetchFn<QueryData, QueryVariables>;
}

export function createLazyGraphqlResultTransform<QueryData, QueryVariables>(
  fetchFn: FetchFn<QueryData, QueryVariables>,
) {
  return (
    queryResult: QueryResult<QueryData, QueryVariables>,
  ): EnhancedLazyGraphqlResult<QueryData, QueryVariables> => {
    const { data, networkStatus, previousData, error } = queryResult;
    return {
      data,
      previousData,
      error: Boolean(error),
      errorMessage: error,
      isLoading: networkStatus < 5, // https://github.com/apollographql/apollo-client/blob/d96f4578f89b933c281bb775a39503f6cdb59ee8/src/core/networkStatus.ts#L4
      fetch: fetchFn,
    };
  };
}
/**
 * Lazy Graphql Hook
 *
 * Wraps useLazyQuery to parse network status and error values in response.
 * @param query GraphQL query statement
 * @param options Apollo useQuery options
 * @returns Response object
 */
export function useLazyGraphql<
  QueryData = any,
  QueryVariables extends OperationVariables = OperationVariables,
>(
  query: DocumentNode | TypedDocumentNode<QueryData, QueryVariables>,
  options?: LazyQueryHookOptions<QueryData, QueryVariables>,
) {
  const [fetch, queryResult] = useLazyQuery<QueryData, QueryVariables>(query, options);
  const { previousData, error, refetch } = queryResult;

  const initialFetch: FetchFn<QueryData, QueryVariables> = (variables) => fetch({ variables });

  const subsequentFetch = (refetch ?? initialFetch) as FetchFn<QueryData, QueryVariables>;

  const { teamName, productName } = useRumContext();
  createErrorMonitor({ name: 'GRAPHQL', teamName, productName })(error);

  return createLazyGraphqlResultTransform<QueryData, QueryVariables>(
    previousData ? subsequentFetch : initialFetch,
  )(queryResult);
}
